import { UpdateSettingsMutationVariables } from '~/data';
import {
  fetchLearnerLanguagePreference,
  logLmsLanguage,
  type LmsAdapter
} from '~/lms';
import { persistSettings } from './settings/persistSettings';
import { useTrainingStore } from './useTrainingStore';

function resolveLocaleIfSupported(
  locale: string,
  available: string[]
): string | null {
  if (available.length === 0) return null;

  const language = locale.replace(/-.*$/, '');
  if (available.includes(locale)) return locale;
  if (available.includes(language)) return language;
  return null;
}

type LocaleLists = {
  textLocales: string[];
  subtitleLocales: string[];
  mediumLocales: string[];
};

type CurrentLocales = {
  textLocale: string;
  subtitleLocale: string;
  mediumLocale: string;
  subtitles: boolean;
};

/**
 * Map LMS language to every learner setting the course supports for that
 * locale — same idea as the onboarding picker (Interface / Captions / Audio).
 */
export function buildLmsLocaleUpdates(
  raw: string,
  lists: LocaleLists,
  current: CurrentLocales
): Partial<UpdateSettingsMutationVariables> | null {
  const { textLocales, subtitleLocales, mediumLocales } = lists;
  const updates: Partial<UpdateSettingsMutationVariables> = {};
  let supportedSomewhere = false;

  const textResolved = resolveLocaleIfSupported(raw, textLocales);
  if (textResolved) {
    supportedSomewhere = true;
    if (current.textLocale !== textResolved) {
      updates.textLocale = textResolved;
    }
  }

  const subtitleResolved = resolveLocaleIfSupported(raw, subtitleLocales);
  if (subtitleResolved) {
    supportedSomewhere = true;
    if (current.subtitleLocale !== subtitleResolved) {
      updates.subtitleLocale = subtitleResolved;
      updates.subtitles = true;
    }
  }

  const mediumResolved = resolveLocaleIfSupported(raw, mediumLocales);
  if (mediumResolved) {
    supportedSomewhere = true;
    if (current.mediumLocale !== mediumResolved) {
      updates.mediumLocale = mediumResolved;
    }
  }

  if (!supportedSomewhere) {
    return null;
  }

  return Object.keys(updates).length > 0 ? updates : {};
}

/**
 * On every LMS launch, apply `cmi.*_preference.language` to learner settings.
 * Each setting (interface / captions / audio) is updated independently when
 * the LMS locale is listed for that dimension on the course.
 */
export async function syncLearnerLanguageFromLms(
  lms: LmsAdapter
): Promise<boolean> {
  if (!lms.isAvailable) return false;

  const state = useTrainingStore.getState();
  if (!state.learnerTrainingId) return false;

  const raw = await fetchLearnerLanguagePreference(lms);
  if (!raw) return false;

  const updates = buildLmsLocaleUpdates(
    raw,
    {
      textLocales: state.textLocales,
      subtitleLocales: state.subtitleLocales,
      mediumLocales: state.mediumLocales
    },
    {
      textLocale: state.textLocale,
      subtitleLocale: state.subtitleLocale,
      mediumLocale: state.mediumLocale,
      subtitles: state.subtitles
    }
  );

  if (updates === null) {
    logLmsLanguage('launch sync skipped — LMS locale not in course', { raw });
    return false;
  }

  if (Object.keys(updates).length === 0) {
    logLmsLanguage('launch sync skipped — already matches LMS locale', {
      raw,
      textLocale: state.textLocale,
      subtitleLocale: state.subtitleLocale,
      mediumLocale: state.mediumLocale
    });
    return false;
  }

  logLmsLanguage('launch sync applying LMS locale', { raw, updates });

  const persisted = await persistSettings({
    learnerTrainingUuid: state.learnerTrainingId,
    textLocale: updates.textLocale ?? state.textLocale,
    subtitleLocale: updates.subtitleLocale ?? state.subtitleLocale,
    mediumLocale: updates.mediumLocale ?? state.mediumLocale,
    subtitles: updates.subtitles ?? state.subtitles,
    auditoryFeedback: state.auditoryFeedback,
    visualFeedback: state.visualFeedback,
    darkMode: state.darkMode,
    autoPlay: state.autoPlay,
    playbackSpeed: state.playbackSpeed
  });

  useTrainingStore.setState(persisted);
  document.documentElement.setAttribute(
    'lang',
    persisted.textLocale || state.textLocale || 'en'
  );

  return true;
}
